import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '../../api/problem'

export type ReportsSectionStatus = 'loading' | 'ok' | 'error' | 'empty'

export interface UseReportsSectionResult<T> {
  status: ReportsSectionStatus
  data: T | null
  message: string | null
  retry: () => void
}

/**
 * `ReportsScreen`'s per-section fetch lifecycle — the same "one section's failure never blanks the
 * rest of the page" posture as `screens/insights/widgets/useWidgetData.ts`, generalized to a plain
 * async `fetcher` (there is no single `insights` widget endpoint backing every Reports card — some
 * are assembled client-side from `case_`/`credit` reads, see `reportsData.ts`).
 */
export function useReportsSection<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  isEmpty: (data: T) => boolean,
  errorMessage = 'Could not load this section. The rest of the report is unaffected.',
): UseReportsSectionResult<T> {
  const [status, setStatus] = useState<ReportsSectionStatus>('loading')
  const [data, setData] = useState<T | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setStatus('loading')
    setMessage(null)
    fetcher(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return
        setData(result)
        setStatus(isEmpty(result) ? 'empty' : 'ok')
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        setStatus('error')
        setData(null)
        setMessage(err instanceof ApiError ? (err.problem.detail ?? err.message) : errorMessage)
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce])

  const retry = useCallback(() => setNonce((n) => n + 1), [])

  return { status, data, message, retry }
}
